import i18n, { t } from "i18n";
import _ from "lodash";
import { CampaignType, CampaignStatus, CampaignAutomaticStatus } from "models";
import { Icon, Popup } from "semantic-ui-react";
import siteSpecifics from "constants/siteSpecific";
import { toast } from "components/Toast/Toast";
import languages from "@cospired/i18n-iso-languages";

export const assetTypeToPlaceholderIcon = (assetType = "", size = "massive") => {
  const type = assetType.split("/").pop().toLowerCase();

  const icons = {
    "jpeg": "file image outline",
    "png": "file image outline",
    "mp4": "file video outline",
    "quicktime": "file video outline",
    "pdf": "file pdf outline",
    "zip": "file archive outline",
    "x-zip-compressed": "file archive outline",
    "x-zip": "file archive outline",
  };

  const iconName = icons[type] || "file image outline"; // Default icon

  return <Icon name={iconName} size={size} style={{ display: "auto" }} />;
};

export const getAssetTypeString = (type) => {
  if (!type || type.toUpperCase() === "UNKNOWN") {
    return "UNKNOWN";
  }
  if (type.includes("/")) {
    return type.split("/")[1].toUpperCase();
  }
  return type.toUpperCase();
};

export const getDataFromUserAttributes = (value) => (value != null ? String(value) : "n/a");

const devEmails = [
  "user.removed@bauermediaoutdoor.com",
  "aida.import@bauermediaoutdoor.com",
  "developer-env-testadmin@mail.com",
  "developer-env-testuser@mail.com",
  "testing-env-testadmin@mail.com",
  "testing-env-testuser@mail.com",
];

export const filterDevUsers = (users) => users.filter((user) => !devEmails.includes(user.email));

export const usersToSelectOptions = (users) =>
  filterDevUsers(users)
    .map(({ id, firstName, lastName, email }) => ({
      key: id,
      value: id,
      text: `${firstName} ${lastName} (${email})`,
    }))
    .sort((a, b) => a.text.localeCompare(b.text));

export const formatFileSizeAndDuration = (fileSize, duration) => {
  if (!fileSize && !duration) return null;

  return [fileSize ? `${fileSize} MB` : "", duration ? `${duration} s` : ""]
    .filter(Boolean)
    .join(" / ");
};

export const getFileSizeLabel = (fileSize, duration, t) => {
  return fileSize && duration
    ? `${t("asset_fileSize")} / ${t("asset_duration")}`
    : fileSize
    ? t("asset_fileSize")
    : duration
    ? t("asset_duration")
    : null;
};

export const showElementFullTextPopup = (ref, text, fontStyle, background) => {
  const font = fontStyle;
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  context.font = font;
  const { width } = context.measureText(text);
  const containerWidth = ref.current?.offsetWidth;
  const result =
    containerWidth && width < containerWidth ? (
      text
    ) : (
      <div className="popup-row">
        <div
          className="popup-part"
          style={{
            background: background ? background : "#ffffff",
            left: containerWidth && containerWidth + 1,
          }}
        >
          ...
        </div>
        <Popup
          trigger={<span className="popup-content">{text}</span>}
          content={text}
          style={{ maxWidth: "700px", wordBreak: "break-all" }}
        />
      </div>
    );
  return result;
};

const DELIVERY_CODE_SYMBOLS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
const deliveryCodeRegex = new RegExp("^[" + DELIVERY_CODE_SYMBOLS + "]+$");
const emailRegex = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;
// Minimum resolution is 50x50 and maximum is 9999x9999
const resolutionRegex = /^([5-9][0-9]|[1-9][0-9][0-9]|[1-9][0-9][0-9][0-9])x([5-9][0-9]|[1-9][0-9][0-9]|[1-9][0-9][0-9][0-9])$/;

export const today = new Date();
export const yesterday = new Date().setDate(today.getDate() - 1);

export const generateDeliveryCode = (length = 10) => {
  let code = "";
  while (length--) {
    code += DELIVERY_CODE_SYMBOLS[Math.floor(Math.random() * DELIVERY_CODE_SYMBOLS.length)];
  }
  return code;
};

/**
 * This function returns languages in a format supported by the date picker.
 */
export const getCurrentLanguage = () => {
  const language = i18n.language;

  switch (language) {
    case "en":
      return "en-US";
    case "fi":
      return "fi-FI";
    case "pl":
      return "pl-PL";
    case "da":
      return "da-DK";
    case "no":
      return "nb-NO";
    case "sv":
      return "sv-SE";
    case "nl-NL":
      return "nl-NL";
    case "nl-BE":
      return "nl-NL";
    case "fr-BE":
      return "fr-FR";
    default:
      return "en-US";
  }
};

const _registeredIsoLocales = new Set();

/**
 * Ensures the i18n-iso-languages locale JSON is registered and returns the locale to use.
 * Falls back to "en".
 */
function _resolveIsoLocale(i18nLanguage) {
  const base = (i18nLanguage || "en").split("-")[0];

  try {
    if (!_registeredIsoLocales.has(base)) {
      // eslint-disable-next-line global-require, import/no-dynamic-require
      const data = require(`@cospired/i18n-iso-languages/langs/${base}.json`);
      languages.registerLocale(data);
      _registeredIsoLocales.add(base);
    }
    return base;
  } catch (err) {
    if (!_registeredIsoLocales.has("en")) {
      // eslint-disable-next-line global-require
      const enData = require(`@cospired/i18n-iso-languages/langs/en.json`);
      languages.registerLocale(enData);
      _registeredIsoLocales.add("en");
    }
    return "en";
  }
}

/**
 * Translate a language code (e.g. "fi") to a label in the current UI language.
 */
export function getTranslatedLanguage(code) {
  const locale = _resolveIsoLocale(i18n.language);
  return languages.getName(code, locale) || code;
}

/**
 * Build Semantic UI Dropdown options (translated) that store ISO codes.
 */
export function getLanguageOptions(i18nLanguage = i18n.language) {
  const locale = _resolveIsoLocale(i18nLanguage);

  return Object.entries(languages.getNames(locale))
    .filter(([code]) => code.length === 2)
    .map(([code, label]) => ({
      key: code,
      value: code,
      text: label,
    }))
    .sort((a, b) => a.text.localeCompare(b.text, locale));
}

export const formatLanguageForAsset = (language, confidenceScore, t) => {
  let text = t("language_not_detected");
  let icon = <Icon name="warning circle" color="red" />;

  if (
    language &&
    language !== "undefined" &&
    typeof confidenceScore === "number" &&
    confidenceScore > 0
  ) {
    const translatedLanguage = getTranslatedLanguage(language);
    if (translatedLanguage) {
      text = `${translatedLanguage} ${confidenceScore}%`;
      icon =
        confidenceScore < 70 ? (
          <Icon name="warning circle" color="yellow" />
        ) : (
          <Icon name="check circle" color="green" />
        );
    }
  }

  return (
    <>
      {text} {icon}
    </>
  );
};

export const formatMotiveMarkerForAsset = (motiveMarker, t) => {
  return (
    <>
      {t(motiveMarker ? "motiveMarker_detected" : "motiveMarker_not_detected")}{" "}
      <Icon
        name={motiveMarker ? "check circle" : "warning circle"}
        color={motiveMarker ? "green" : "red"}
      />
    </>
  );
};

export const formatDPIForAsset = (dpi) => {
  if (dpi === "None" || dpi === null) {
    return (
      <>
        N/A <Icon name="warning circle" color="yellow" />
      </>
    );
  }
  const icon =
    dpi <= 30
      ? { name: "warning circle", color: "red" }
      : dpi < 80
      ? { name: "warning circle", color: "yellow" }
      : { name: "check circle", color: "green" };

  return (
    <>
      {dpi} <Icon {...icon} />
    </>
  );
};

export const getDigitalCampaignContactInfo = (topLevel) => {
  topLevel = siteSpecifics[topLevel] ? topLevel : "fi";
  return siteSpecifics[topLevel]["contacts"]["digital_campaigns"]["email"];
};

export const getPrintCampaignContactInfo = (topLevel) => {
  topLevel = siteSpecifics[topLevel] ? topLevel : "fi";
  return siteSpecifics[topLevel]["contacts"]["print_campaigns"]["email"];
};

const SUPPORTED_COUNTRY_CODES = new Set(["fi", "pl", "se", "no", "dk", "nl", "be"]);

export const getCountryCode = () => {
  const host = window.location.hostname;

  if (host === "localhost") {
    // This is the domain returned when running on localhost.
    // You can replace "fi" with another country code, such as "pl", to test a different domain on localhost.
    // Note that the data will always come from the same development environment.
    return "fi";
  }

  // Extract subdomain (before first dot)
  const subdomain = host.split(".")[0];

  if (subdomain.includes("dev") || subdomain.includes("laatikko")) {
    return "dev";
  }

  // Extract segments like ['content', 'pl', 'test']
  const parts = subdomain.split("-");

  // Check each segment for known country code
  for (const part of parts) {
    if (SUPPORTED_COUNTRY_CODES.has(part)) {
      return part;
    }
  }

  // Fallback for unknown hosts
  console.warn(`Could not determine country code from host: ${host}`);
  return "fi";
};

export const getDeliveryDeadline = (date, campaignType) => {
  let today = new Date();
  today = today.setHours(0, 0, 0, 0);
  date = new Date(date);
  date = new Date(date.getTime());
  let day = date.getDay();
  const numberOfDays = campaignType === CampaignType.DIGITAL ? -4 : -10;
  date.setDate(
    date.getDate() +
      numberOfDays +
      (day === 6 ? 2 : +!day) +
      Math.floor((numberOfDays - 1 + (day % 6 || 1)) / 5) * 2
  );

  return date.setHours(0, 0, 0, 0) >= today ? date : new Date(today);
};

const YMDCountries = new Set(["lt", "hu", "se"]); // Add here the country codes for countries that use the YMD date format
const countryCode = getCountryCode();

export const formatDate = (date) => {
  if (!date) return null;
  const hasTime = date.length > 10;
  const parsedDate = new Date(date);
  const day = String(parsedDate.getDate()).padStart(2, "0");
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const year = parsedDate.getFullYear();
  const hours = parsedDate.getHours();
  const minutes = parsedDate.getMinutes();
  const time = hasTime
    ? ` ${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`
    : "";
  return YMDCountries.has(countryCode)
    ? `${year}-${month}-${day}${time}`
    : `${day}-${month}-${year}${time}`;
};

export const getDateFormat = () => {
  return YMDCountries.has(countryCode) ? "YYYY-MM-DD" : "DD-MM-YYYY";
};
export const validatePhoneNumber = (v) => /^\+?[\d\s-]+/.test(v);

export const validateDeliveryCode = (v) => deliveryCodeRegex.test(v);

export const validateOrderlineID = (v) =>
  !v || /^[a-zA-Z0-9]+$|[a-zA-Z0-9]+(?:_[a-zA-Z0-9]+)+$/.test(v); //* /^\d+$|\d+(?:_\d+)+$/ validates only digits or seriesof digits separated by "_" **/^\d+$|^\d+-\d+$/ to check for only two orderlineID's separated by "-"

export const validateEmail = (v) => !v || emailRegex.test(v);

export const validateResolution = (v) => resolutionRegex.test(v);

export const getUserNameEmailString = (user) => {
  return user
    ? `${user.firstName} ${user.lastName}${user.firstName && user.lastName ? ", " : ""}${
        user.email
      }`
    : null;
};

export const getContactsInfo = (ids, users) => {
  return ids
    .map((id) => {
      const user = users.find((user) => user.id === id);
      return getUserNameEmailString(user);
    })
    .filter(Boolean);
};

// Gets date in AWSDate format "YYYY-MM-DD"
export const getAWSDate = (dateInput) => {
  const date = new Date(dateInput);

  const day = date.getDate();
  // .getMonth() returns a value from 0-11 -> adding 1 gives the correct month number
  const month = date.getMonth() + 1;

  // AWSDate requires month and day to have 0 as prefix if they are less than 10
  return `${date.getFullYear()}-${month < 10 ? "0" + month : month}-${day < 10 ? "0" + day : day}`;
};

export const genericSearch = (searchData, searchCriteria) => {
  const findMatches = (searchData) => {
    if (searchData && searchCriteria) {
      if (
        searchData === searchCriteria ||
        (searchData.toString() &&
          searchCriteria.toString() &&
          searchData.toString().toLowerCase().includes(searchCriteria.toString().toLowerCase()))
      )
        return true;
      return searchData === Object(searchData) && Object.values(searchData).some(findMatches);
    }
  };
  return (Array.isArray(searchData) ? searchData : Object.values(searchData)).filter(findMatches);
};

/**
 * Checks if given Required Resolution (RR) has been delivered
 * @param {object} requiredResolution         - The RR to check
 * @param {object[]} nativeResolutions        - Native resolutions in the campaign under the RR
 * @param {string[]} assetResolutionsToSearch - Asset resolutions to search
 * @returns {boolean}
 */
export const getIsRequiredResolutionDelivered = (
  requiredResolution,
  nativeResolutions,
  assetResolutionsToSearch
) => {
  // Check if RR is delivered directly
  let isRequiredResolutionDelivered = _.includes(
    assetResolutionsToSearch,
    requiredResolution.value
  );
  // If RR is not delivered directly, check if all Native Resolutions under it are
  if (!isRequiredResolutionDelivered) {
    isRequiredResolutionDelivered = _.every(nativeResolutions, (nativeRes) =>
      _.includes(assetResolutionsToSearch, nativeRes.value)
    );
  }

  return isRequiredResolutionDelivered;
};

// export const formatAssetMetadata = (metadata) => {
//   const { qc_result, qc_duration_error, qc_framerate_error, qc_resolution_error } = metadata;
//   if (qc_duration_error || qc_framerate_error || qc_resolution_error) {
//     return (
//       <List as="ol">
//         <List.Item as="li" value="-">
//           {t(qc_result, { ns: "common" })}:
//           <List.List as="ol">
//             {qc_duration_error && (
//               <List.Item as="li" value="-">
//                 {t(qc_duration_error, { ns: "common" })}
//               </List.Item>
//             )}
//             {qc_framerate_error && (
//               <List.Item as="li" value="-">
//                 {t(qc_framerate_error, { ns: "common" })}
//               </List.Item>
//             )}
//             {qc_resolution_error && (
//               <List.Item as="li" value="-">
//                 {t(qc_resolution_error, { ns: "common" })}
//               </List.Item>
//             )}
//           </List.List>
//         </List.Item>
//       </List>
//     );
//   } else if (qc_result) return t(qc_result, { ns: "common" });
//   else return "-";
// };

export const formatLanguagesForCampaign = (languagesArray) => {
  if (!languagesArray || languagesArray.length === 0) return "-";
  const languageNames = languagesArray.map((lang) => getTranslatedLanguage(lang));
  return languageNames.join(", ");
};

export const formatDuration = (durations) => {
  const language = i18n.language;
  const listFormatter = new Intl.ListFormat(language, {
    style: "long",
    type: "disjunction",
  });
  return Array.isArray(durations) ? listFormatter.format(durations) : durations;
};

export const formatAssetMetadata = (metadata, durations) => {
  // Handle empty or missing metadata
  if (!metadata || Object.keys(metadata).length === 0) {
    return t("ad_copy_upload_failed", { ns: "common" });
  }
  const { qc_result } = metadata;

  if (qc_result === "qc_duration_error") {
    const safeDurations = durations.length > 0 ? durations : ["5", "6", "10"];
    const formattedDuration = formatDuration(safeDurations);
    return t("qc_duration_error", {
      duration: formattedDuration,
      ns: "common",
    });
  }
  return t(qc_result, { ns: "common" });
};

// Takes a date and returns the previous day's date
export const getPreviousDaysDate = (date) => {
  try {
    return new Date(date.setDate(new Date(date).getDate() - 1));
  } catch (error) {
    console.error(error);
    return undefined;
  }
};

/**
 * Gets file name from asset's media
 * @param {string} media - The media string of the asset. The file name is assumed to be the last part of the /-delimited string.
 * @returns {string} - The filename
 */
export const getFileNameFromAssetMedia = (media) => {
  try {
    // Split input e.g. assets/ce9d5098-9635-4af7-8875-3d74a48d031d/d445f845-87b2-43f1-b177-39e62b34e560/IMG_2462_1aaa.jpg -> returns IMG_2462_1aaa.jpg
    const mediaSplit = _.split(media, "/");
    return mediaSplit[mediaSplit.length - 1];
  } catch (error) {
    console.error(error);
    throw new Error("Getting file name from asset media failed.");
  }
};

/**
 * Downloads a file from url. Solution adapted from https://stackoverflow.com/a/29606450
 * @param {string} url - URL of the file to be downloaded
 */
export const downloadFromUrl = (url) => {
  const a = document.createElement("a");
  if (url) a.href = url;
  a.target = "_parent";

  // Add a to the doc for click to work
  (document.body || document.documentElement).appendChild(a);
  if (a.click && a.href) {
    // The click method is supported by most browsers. TODO possible backup method?
    a.click();
  }
  // Delete the temporary link
  a.parentNode.removeChild(a);
};

export const handleDownloadClick = async (e, mediaUrl, t) => {
  e.preventDefault();
  try {
    // Check if the file exists before downloading
    const response = await fetch(mediaUrl, {
      method: "GET",
      headers: { Range: "bytes=0-0" },
    });
    if (response.ok) {
      // If the file exists, trigger the download
      window.location.href = mediaUrl;
    } else {
      toast("error", t("asset_not_found"));
    }
  } catch (error) {
    toast("error", `${t("asset_error")}: ${error}`);
  }
};

// Return true if asset is already processed
export const isAssetProcessed = (asset, processedAssets) => {
  const result = processedAssets.filter((item) => item.media.includes(asset.id));
  return result.length > 0;
};

export const isZipAsset = (asset) => asset?.type?.includes("zip");

export const getProcessedAssetsForAsset = (asset, processedAssets) => {
  const relatedProcessedAssets = processedAssets.filter((item) => item.media.includes(asset.id));
  return relatedProcessedAssets.length > 0 || !isZipAsset(asset) ? relatedProcessedAssets : [asset];
};

export const isShownInProcessedAssets = (asset, processedAssets) =>
  isAssetProcessed(asset, processedAssets) || isZipAsset(asset);

export const formatInformationLength = (infoText, maxLenght) => {
  const shortInfoText =
    infoText && infoText.length > maxLenght ? `${infoText.slice(0, maxLenght)}...` : infoText;
  return shortInfoText;
};

export const getCampaignStatusOptions = (campaignType, currentStatus) => {
  const notSelectableStatus =
    campaignType === "DIGITAL" ? CampaignStatus.SENT_TO_PRINT : CampaignStatus.SCHEDULED;

  const automaticStatuses = new Set(Object.values(CampaignAutomaticStatus));

  const baseStatuses = Object.values(CampaignStatus).filter(
    (status) => status !== notSelectableStatus && !automaticStatuses.has(status)
  );

  const statusSet = new Set(baseStatuses);

  if (currentStatus) {
    statusSet.add(currentStatus);
  }

  // Selcting "NULL" will revert to latest automatic status
  statusSet.add("NULL");

  return Array.from(statusSet).map((status) => ({
    key: status,
    value: String(status),
    text: status.replace(/_/g, " "),
  }));
};
